// ─── Helpers ─────────────────────────────────────────────────────────────────

// Escape customer-controlled text before it goes into the letter HTML (J6): this
// document is built as a string and written via document.write into a new window, so
// a customer whose name/contact is `<img src=x onerror=...>` would otherwise run script
// in the staff member's session.
function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function formatDateShort(dateStr) {
  if (!dateStr) return '...............';
  const d = new Date(dateStr);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = String(d.getFullYear() + 543); // พ.ศ.
  return `${dd}/${mm}/${yyyy}`;
}

function formatDateThai(dateStr) {
  if (!dateStr) return '...............';
  const d = new Date(dateStr);
  const months = [
    'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
    'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม',
  ];
  return `${d.getDate()} ${months[d.getMonth()]} พ.ศ. ${d.getFullYear() + 543}`;
}

function formatThb(amount) {
  if (amount === null || amount === undefined) return '0';
  const n = Number(amount);
  if (n % 1 === 0) return n.toLocaleString('th-TH');
  const s = n.toFixed(2);
  return s.endsWith('.00') ? n.toLocaleString('th-TH') : s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function formatYen(amount) {
  return Math.round(Number(amount)).toLocaleString('th-TH');
}

function formatRate(rate) {
  return parseFloat(rate).toString();
}

function extractProductCode(url) {
  if (!url) return '-';
  const clean = url.replace(/\/$/, '');
  const parts = clean.split('/');
  return parts[parts.length - 1] || url;
}

function calcOrder(order) {
  const { amount, qty, deliveryFeeJp, isCod, isDown, downRate, productRate, transactionFee, websiteTransactionFee } = order;
  const yenRaw = amount * qty + (isCod ? 0 : (deliveryFeeJp || 0));
  const fee = transactionFee || 0;
  const websiteFee = websiteTransactionFee || 0;
  if (isDown) {
    const serviceFee = yenRaw * ((downRate || 0) / 100);
    const payYen = yenRaw / 2 + serviceFee;
    const thbPay = payYen * productRate;
    const thbFull = yenRaw * productRate;
    return { yenRaw, thbPay, thbFull, fee, websiteFee, net: thbPay + fee, isDown: true };
  }
  const thb = yenRaw * productRate;
  return { yenRaw, thbPay: thb, thbFull: null, fee, websiteFee, net: thb + fee, isDown: false };
}

// ─── Attachment pages: 2 images per page ────────────────────────────────────
// startPage = page number of first page in this attachment
// totalPages = overall total pages in the document
function renderAttachmentPages(images, title, subtitle, startPage, totalPages) {
  const IMGS_PER_PAGE = 2;

  if (!images || images.length === 0) {
    return `<div class="page">
  <div class="pg-num">${startPage}/${totalPages}</div>
  <div class="att-hd">
    <h2>${title}</h2>
    <h3>${subtitle}</h3>
  </div>
  <p class="no-img">[ ไม่มีรูปภาพแนบ ]</p>
</div>`;
  }

  // split into chunks of IMGS_PER_PAGE
  const chunks = [];
  for (let i = 0; i < images.length; i += IMGS_PER_PAGE) {
    chunks.push(images.slice(i, i + IMGS_PER_PAGE));
  }

  return chunks.map((chunk, pageIdx) => {
    const currentPage = startPage + pageIdx;
    const headerHtml = pageIdx === 0
      ? `<div class="att-hd">
    <h2>${title}</h2>
    <h3>${subtitle}</h3>
  </div>`
      : `<div class="att-hd-cont">
    <h3>${title} (ต่อ)</h3>
  </div>`;

    const imgsHtml = chunk.map((img, i) => {
      const globalIdx = pageIdx * IMGS_PER_PAGE + i;
      const capText = img.caption && img.caption.trim()
        ? img.caption.trim()
        : `รูปที่ ${globalIdx + 1}`;
      return `<div class="img-item">
      <img src="${img.dataUrl}" alt="${capText}" />
      <div class="img-cap">${capText}</div>
    </div>`;
    }).join('\n    ');

    return `<div class="page">
  <div class="pg-num">${currentPage}/${totalPages}</div>
  ${headerHtml}
  <div class="img-grid">
    ${imgsHtml}
  </div>
</div>`;
  }).join('\n\n');
}

// ─── Shop contact channels ────────────────────────────────────────────────────

const SHOP_CONTACTS = {
  tama: {
    facebook: 'https://www.facebook.com/tamachan.official',
    line: '@tamachan',
  },
  default: {
    facebook: 'https://www.facebook.com/smileshippingth',
    line: '@smile-shipping',
  },
};

function getShopContact(shopName) {
  const name = (shopName || '').toLowerCase();
  if (name.includes('tama')) return SHOP_CONTACTS.tama;
  return SHOP_CONTACTS.default;
}

// ─── Main export ─────────────────────────────────────────────────────────────

export function generateDebtLetterHtml({
  letterData, att2Images, att3Images,
  firstDate, secondDate,
  logoBase64, letterName, shopName,
  followUpCount = 3,
}) {
  const { customer, orders } = letterData;

  const today     = formatDateThai(new Date());
  const date1Thai = formatDateThai(firstDate);
  const date2Thai = formatDateThai(secondDate);
  const custName  = escapeHtml(letterName || customer.customerName || customer.contact || '-');
  const contact   = getShopContact(shopName);

  // ── totals ──
  let totalYen = 0, totalThb = 0, totalFee = 0, totalNet = 0;
  const orderRows = orders.map((order, idx) => {
    const c = calcOrder(order);
    totalYen += c.yenRaw;
    totalThb += c.thbPay;
    totalFee += c.fee;
    totalNet += c.net;

    const code      = escapeHtml(extractProductCode(order.url));
    const dateDisp  = formatDateShort(order.orderDate);
    const codeBadge = c.isDown ? `<span class="badge-down">มัดจำ 50%</span>` : '';
    const thbCell   = c.isDown && c.thbFull
      ? `${formatThb(c.thbPay)}<div class="full-note">เต็ม ${formatThb(c.thbFull)}</div>`
      : formatThb(c.thbPay);

    return `<tr>
      <td>${idx + 1}</td>
      <td>${dateDisp}</td>
      <td class="td-l">${code}${codeBadge}</td>
      <td>${formatYen(c.yenRaw)}</td>
      <td>${formatRate(order.productRate)}</td>
      <td>${thbCell}</td>
      <td>${formatThb(c.fee)}${c.websiteFee > 0 ? `<div class="full-note">รวมค่าบริการเว็บไซต์ ${formatThb(c.websiteFee)}฿</div>` : ''}</td>
      <td class="td-b">${formatThb(c.net)}</td>
    </tr>`;
  }).join('');

  const logo    = logoBase64 ? `<img src="${logoBase64}" class="logo-hd" alt="logo">` : '';
  const logoSig = logoBase64 ? `<img src="${logoBase64}" class="logo-sig" alt="logo">` : '';
  const logoTbl = '';

  // ── Page number calculation ──
  const att2PageCount = (att2Images && att2Images.length > 0)
    ? Math.ceil(att2Images.length / 2) : 1;
  const att3PageCount = (att3Images && att3Images.length > 0)
    ? Math.ceil(att3Images.length / 2) : 1;
  // page 1 = letter body, page 2 = signature, page 3 = table, pages 4+ = attachments
  const totalPages  = 3 + att2PageCount + att3PageCount;
  const att2Start   = 4;
  const att3Start   = 4 + att2PageCount;

  return `<!DOCTYPE html>
<html lang="th">
<head>
<meta charset="utf-8">
<title>หนังสือทวงถามหนี้ — ${custName}</title>
<style>
/* ═══════════════════════════════════════════════════════════════════
   PRINCIPLE: Base CSS defines the EXACT print-ready sizes.
   Screen preview = print output. @media print only removes decoration.
   ═══════════════════════════════════════════════════════════════════ */

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

body {
  font-family: 'TH Sarabun New', 'Sarabun', Tahoma, Arial, sans-serif;
  font-size: 14px;
  color: #000;
  /* Screen-only: gray background to show page boundaries */
  background: #909090;
}

/* ── Print button (screen only) ── */
.fab {
  position: fixed; top: 14px; right: 14px; z-index: 9999;
  background: #1d4ed8; color: #fff; border: none;
  padding: 9px 20px; border-radius: 8px; font-size: 14px;
  cursor: pointer; font-family: inherit;
  box-shadow: 0 3px 10px rgba(0,0,0,.3);
}
.fab:hover { background: #1e40af; }

/* ── A4 page — sizes here are the canonical print sizes ── */
.page {
  width: 210mm;
  min-height: 297mm;
  padding: 8mm 18mm 8mm 18mm;
  position: relative;
  background: #fff;
  /* Screen-only decoration */
  margin: 6mm auto;
  box-shadow: 0 2px 16px rgba(0,0,0,.35);
}

/* ══ LETTER PAGE ══ */

/* header */
.hd {
  text-align: center;
  padding-bottom: 6px;
  border-bottom: 2.5px solid #000;
}
.logo-hd {
  width: 55px; height: 55px;
  object-fit: contain; display: block; margin: 0 auto 3px;
}
.hd-co   { font-size: 18px; font-weight: bold; line-height: 1.25; }
.hd-en   { font-size: 14px; font-weight: bold; }
.hd-info { font-size: 12px; color: #222; line-height: 1.45; margin-top: 2px; }

/* meta */
.spacer    { height: 5px; }
.spacer-lg { height: 11px; } /* ≈ 1 blank line at 13.5px/1.6 lh */
.meta-date { text-align: right; font-size: 14px; margin-bottom: 0; }
.lbl       { font-weight: bold; }
.subject   { font-size: 14px; font-weight: bold; margin: 0 0 2px 0; }
.to-line   { font-size: 14px; margin: 0; }

/* attachments list */
.att-title { font-weight: bold; font-size: 14px; margin-bottom: 2px; }
.att-list  {
  padding-left: 20px; margin: 2px 0 0 0;
  line-height: 1.6; font-size: 14px;
}

/* body paragraphs */
.para {
  font-size: 14px;
  line-height: 1.6;
  margin: 3px 0;
  text-align: left;
  text-indent: 2em;
}
.para-noindent { text-indent: 0; }

/* bank list */
.bank {
  margin: 3px 0 5px 12px;
  font-size: 14px;
  line-height: 1.6;
  list-style: none;
}
.bank li::before { content: "● "; font-weight: bold; }

/* slip confirmation box */
.slip-box {
  margin: 5px 0 5px 0;
  padding: 6px 12px;
  background: #f0f9ff;
  border-left: 3px solid #0ea5e9;
  border-radius: 4px;
  font-size: 13.5px;
  line-height: 1.65;
}
.slip-box p  { margin: 0 0 2px 0; }
.slip-box ul { padding-left: 16px; margin: 2px 0 0 0; list-style: none; }
.slip-box ul li::before { content: "● "; font-weight: bold; }
.slip-box a  { color: #1d4ed8; text-decoration: none; }

/* signature — right-aligned block, internal text centered */
.sig {
  margin-top: 6px;
  margin-left: auto;
  width: 55%;
  text-align: center;
  font-size: 14px;
  line-height: 1.75;
  page-break-inside: avoid;
}
.sig-dotline {
  display: inline-block;
  min-width: 190px;
  border-bottom: 1px dotted #555;
}
.logo-sig { width: 50px; height: 50px; object-fit: contain; margin-top: 3px; }

/* ══ TABLE PAGE ══ */
.page-ttl {
  text-align: center;
  font-size: 16px;
  font-weight: bold;
  line-height: 1.4;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 2px solid #000;
}
table { width: 100%; border-collapse: collapse; font-size: 11.5px; }
th, td { border: 1px solid #555; padding: 4px 3px; text-align: center; vertical-align: middle; }
th { background: #e8e8e8; font-weight: bold; line-height: 1.3; }
td.td-l { text-align: left; padding-left: 5px; }
td.td-b { font-weight: bold; }
tfoot td { background: #f2f2f2; font-weight: bold; }
.badge-down {
  display: block; font-size: 9.5px; color: #b91c1c;
  background: #fee2e2; border-radius: 3px; padding: 1px 3px;
  margin-top: 2px; width: fit-content;
}
.full-note { font-size: 9.5px; color: #6b7280; line-height: 1.2; margin-top: 1px; }
.tbl-logo  { position: absolute; bottom: 10mm; right: 16mm; }

/* ══ IMAGE PAGES ══ */
.att-hd {
  text-align: center;
  margin-bottom: 12px;
  padding-bottom: 6px;
  border-bottom: 2px solid #000;
}
.att-hd h2 { font-size: 18px; font-weight: bold; }
.att-hd h3 { font-size: 14px; font-weight: bold; margin-top: 2px; color: #333; }

.img-grid      { display: grid; grid-template-columns: 1fr; gap: 12px; }
.img-grid.solo { grid-template-columns: 1fr; max-width: 80%; margin: 0 auto; }
.img-grid.two  { grid-template-columns: 1fr; }

.img-item {
  border: 1px solid #bbb; padding: 8px;
  break-inside: avoid; page-break-inside: avoid;
}
.img-item img {
  width: 100%; height: auto; display: block;
  max-height: 390px; object-fit: contain; background: #f8f8f8;
}
.img-cap {
  font-size: 12px; text-align: center;
  color: #444; margin-top: 5px; font-style: italic;
}
.no-img { color: #aaa; text-align: center; padding: 50px 0; font-size: 14px; }

/* ── Page number (bottom-right of each .page) ── */
.pg-num {
  position: absolute;
  bottom: 5mm;
  right: 18mm;
  font-size: 10.5px;
  color: #888;
}

/* ── Continuation header for attachment pages ── */
.att-hd-cont {
  text-align: center;
  margin-bottom: 10px;
  padding-bottom: 4px;
  border-bottom: 1px solid #bbb;
}
.att-hd-cont h3 { font-size: 13px; color: #555; font-weight: bold; }

/* ══ PRINT MEDIA — only remove screen decoration ══ */
@page { size: A4 portrait; margin: 0; }

@media print {
  /* Force background colors / images to print */
  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }

  html, body {
    background: #fff !important;
    width: 210mm;
    margin: 0; padding: 0;
  }

  /* Remove screen-only decoration */
  .fab  { display: none !important; }
  .page {
    margin: 0;
    box-shadow: none;
    min-height: 297mm; /* keep so pg-num absolute stays at physical page bottom */
    page-break-after: always;
  }

  /* Keep signature as one block */
  .sig { page-break-inside: avoid; }
}
</style>
</head>
<body>

<button class="fab" onclick="window.print()">🖨️ พิมพ์ / บันทึก PDF</button>

<!-- ══════════════ PAGE 1: LETTER ══════════════ -->
<div class="page">
  <div class="pg-num">1/${totalPages}</div>

  <div class="hd">
    ${logo}
    <div class="hd-co">บริษัท สไมล์ เอ็นเอสจี จำกัด</div>
    <div class="hd-en">(Smile NSG Co., Ltd.)</div>
    <div class="hd-info">48/708 ซ.เสรีไทย41 แขวงคลองกุ่ม เขตบึงกุ่ม กรุงเทพฯ 10240</div>
    <div class="hd-info">เลขประจำตัวผู้เสียภาษี: 0105567149114 &nbsp;|&nbsp; โทรศัพท์: 094-924-1664</div>
  </div>

  <div class="spacer"></div>

  <div class="meta-date"><span class="lbl">วันที่:</span> ${today}</div>

  <div class="spacer-lg"></div>

  <div class="subject"><span class="lbl">เรื่อง:</span> ขอให้ชำระหนี้ค่าสินค้าและค่าบริการ (Notice of Payment)</div>
  <div class="to-line"><span class="lbl">เรียน:</span> คุณ ${custName}</div>

  <div class="spacer-lg"></div>

  <div class="att-title">สิ่งที่ส่งมาด้วย:</div>
  <ol class="att-list">
    <li>รายละเอียดการคำนวณค่าสินค้าและค่าบริการ (เอกสารแนบท้าย 1)</li>
    <li>สำเนาหลักฐานการยืนยันการลงประมูล/สั่งซื้อสินค้า (เอกสารแนบท้าย 2)</li>
    <li>สำเนาหลักฐานการติดต่อและทวงถามที่ผ่านมา (เอกสารแนบท้าย 3)</li>
  </ol>

  <div class="spacer-lg"></div>

  <p class="para">ตามที่ท่านได้ใช้บริการของ บริษัท สไมล์ เอ็นเอสจี จำกัด ในฐานะตัวแทนสั่งซื้อและประมูลสินค้าจากประเทศญี่ปุ่น โดยท่านได้ตกลงสั่งซื้อและประมูลสินค้าตามรายการที่ระบุในเอกสารแนบท้าย 1 ซึ่งบริษัทฯ ได้ดำเนินการสั่งซื้อจนสำเร็จเสร็จสิ้นแล้ว นั้น</p>

  <p class="para">บริษัทฯ ได้ทำการสรุปยอดรวมค่าสินค้า ค่าขนส่ง และค่าบริการที่ท่านต้องชำระ เป็นจำนวนเงินทั้งสิ้น <strong>${formatThb(totalNet)} บาท</strong> และได้ทำการแจ้งให้ท่านทราบพร้อมทั้งติดตามทวงถามการชำระเงินมาแล้ว <strong>รวม ${followUpCount} ครั้ง</strong> เมื่อวันที่ <strong>${date1Thai}</strong> และวันที่ <strong>${date2Thai}</strong> แต่จนถึงปัจจุบัน บริษัทฯ ยังไม่ได้รับชำระเงินจำนวนดังกล่าวจากท่านแต่อย่างใด</p>

  <p class="para">ดังนั้น โดยหนังสือฉบับนี้บริษัทฯ จึงขอให้ท่านดำเนินการชำระหนี้จำนวน <strong>${formatThb(totalNet)} บาท</strong> ให้เสร็จสิ้นภายใน <strong>7 วัน</strong> นับแต่วันที่ได้รับหนังสือฉบับนี้</p>

  <div class="spacer-lg"></div>

  <p class="para para-noindent">โดยสามารถโอนเงินผ่านช่องทางดังต่อไปนี้:</p>
  <ul class="bank">
    <li><strong>ชื่อบัญชี:</strong> บริษัท สไมล์ เอ็นเอสจี จำกัด</li>
    <li><strong>ธนาคาร:</strong> ธนาคาร ไทยพาณิชย์ (SCB)</li>
    <li><strong>เลขที่บัญชี:</strong> 429-190745-9</li>
  </ul>

  <div class="spacer-lg"></div>

  <div class="slip-box">
    <p>เพื่อยืนยันการชำระเงิน หากโอนเงินแล้วรบกวนส่งสลิปเข้ามาทางช่องทางดังต่อไปนี้</p>
    <ul>
      <li><strong>Facebook Page :</strong> <a href="${contact.facebook}">${contact.facebook}</a></li>
      <li><strong>LINE Official Account :</strong> ${contact.line}</li>
    </ul>
  </div>

  <div class="spacer-lg"></div>

  <p class="para para-noindent">ทั้งนี้ หากท่านมีข้อสงสัยหรือประสงค์โต้แย้งยอดหนี้ดังกล่าว กรุณาติดต่อบริษัทฯ ทางช่องทางข้างต้นก่อนครบกำหนดระยะเวลาที่กำหนด เพื่อให้บริษัทฯ ดำเนินการตรวจสอบและชี้แจงข้อมูลให้ท่านทราบต่อไป</p>

  <div class="spacer-lg"></div>

  <p class="para">หากพ้นกำหนดระยะเวลาดังกล่าวและท่านยังคงเพิกเฉย บริษัทฯ ขอสงวนสิทธิ์ในการระงับการให้บริการและบันทึกประวัติการผิดนัดชำระหนี้ของท่านในระบบของบริษัทฯ ทันที และบริษัทฯ จะมีความจำเป็นต้องส่งเรื่องให้ทนายความเพื่อฟ้องร้องดำเนินคดีทางแพ่งตามกฎหมาย เพื่อบังคับชำระหนี้รวมถึงเรียกร้องดอกเบี้ยผิดนัดชำระและค่าเสียหายอื่นๆ ที่เกี่ยวข้องต่อไป</p>

</div>

<!-- ══════════════ PAGE 2: LETTER (ต่อ) — ขอแสดงความนับถือ ══════════════ -->
<div class="page">
  <div class="pg-num">2/${totalPages}</div>

  <p class="para">บริษัทฯ หวังเป็นอย่างยิ่งว่าจะได้รับความร่วมมือจากท่านในการชำระหนี้ดังกล่าวให้เรียบร้อย เพื่อหลีกเลี่ยงขั้นตอนทางกฎหมายที่อาจทำให้ท่านต้องเสียเวลาและค่าใช้จ่ายเพิ่มเติม</p>

  <!-- signature -->
  <div class="sig">
    <div>ขอแสดงความนับถือ</div>
    <br>
    <div>(ลงชื่อ)&nbsp;<span class="sig-dotline">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span></div>
    <div>(นายศตพล ลองสกุล)</div>
    <div><strong>กรรมการผู้จัดการ</strong></div>
    <div><strong>บริษัท สไมล์ เอ็นเอสจี จำกัด</strong></div>
    ${logoBase64 ? `<div>${logoSig}</div>` : ''}
  </div>

</div>

<!-- ══════════════ PAGE 3: ATTACHMENT 1 — TABLE ══════════════ -->
<div class="page">
  <div class="pg-num">3/${totalPages}</div>
  <div class="page-ttl">
    เอกสารแนบท้าย 1<br>
    รายละเอียดการคำนวณค่าสินค้าและค่าบริการ
  </div>

  <table>
    <thead>
      <tr>
        <th style="width:5%">ลำดับ</th>
        <th style="width:10%">วันที่ทำ<br>รายการ</th>
        <th style="width:20%">รายการสินค้า</th>
        <th style="width:13%">ยอดค่าสินค้า<br>และค่าส่ง (¥)</th>
        <th style="width:10%">เรท<br>สั่งซื้อ</th>
        <th style="width:13%">ยอดเงิน<br>บาท (฿)</th>
        <th style="width:13%">ค่าบริการ<br>สั่งซื้อ (฿)</th>
        <th style="width:16%">รวมสุทธิ (฿)</th>
      </tr>
    </thead>
    <tbody>${orderRows}</tbody>
    <tfoot>
      <tr>
        <td colspan="3">รวมทั้งสิ้น</td>
        <td>${formatYen(totalYen)}</td>
        <td>-</td>
        <td>${formatThb(totalThb)}</td>
        <td>${formatThb(totalFee)}</td>
        <td>${formatThb(totalNet)}</td>
      </tr>
    </tfoot>
  </table>

  ${logoTbl}
</div>

<!-- ══════════════ ATTACHMENT 2 (1+ pages, 2 imgs/page) ══════════════ -->
${renderAttachmentPages(att2Images, 'เอกสารแนบท้าย 2', 'หลักฐานการยืนยันการลงประมูลหรือการสั่งซื้อ', att2Start, totalPages)}

<!-- ══════════════ ATTACHMENT 3 (1+ pages, 2 imgs/page) ══════════════ -->
${renderAttachmentPages(att3Images, 'เอกสารแนบท้าย 3', 'หลักฐานการทวงถามหนี้ที่ผ่านมา', att3Start, totalPages)}

</body>
</html>`;
}
